"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { navigationConfig, drawerNavigation } from "@shared/src/config/navigation";
import { Button } from "@shared/src/components/ui/button";
import { HomeIcon } from "@shared/src/components/assets/icons";
import { BsThreeDots } from "react-icons/bs";
import { cn } from "@shared/src/utils/cn";
import { Drawer, DrawerTrigger } from "@shared/src/components/ui/drawer";
import { MoreDrawer } from "@shared/src/components/shared/MoreDrawer";
import { useState, useCallback } from "react";
import { useAuthStore } from "@shared/src/stores/useAuthStore";

export function BottomNavbar() {
  const pathname = usePathname();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { activeAccount } = useAuthStore();

  const closeAllModals = useCallback(() => {
    setIsSheetOpen(false);
    setIsDrawerOpen(false);
  }, []);

  // Update handler to use closeAllModals
  const handleNavigationAction = useCallback(() => {
    closeAllModals();
  }, [closeAllModals]);

  // Filter navigation items we want to show
  const visibleNavItems = navigationConfig.filter(item => {
    if (item.label === "Orders") {
      return false; // We'll handle Orders separately
    }
    return ["Quick Trade", "P2P"].includes(item.label);
  });

  const ordersItem = navigationConfig.find(item => item.label === "Orders");

  const activeItemClasses = "!text-text-primary font-extrabold scale-125 transition-transform duration-200";
  const inactiveItemClasses = "!text-text-tertiary scale-90 transition-transform duration-200";
  const activeLabelClasses = "text-[10px] text-text-primary font-extrabold";
  const inactiveLabelClasses = "text-[10px] text-text-tertiary";

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-surfaces-secondary bg-surfaces-bg-primary md:hidden">
      <div className="px-6 py-5">
        <div className={cn("grid items-center", activeAccount ? "grid-cols-5" : "grid-cols-2")}>
          {/* Home - Only show if authenticated */}
          {activeAccount && (
            <div className="flex flex-col items-center gap-1">
              <Button
                variant="ghost-clean"
                size="icon"
                asChild
                className={cn(pathname === "/dashboard" ? activeItemClasses : inactiveItemClasses, "p-0 h-6")}
              >
                <Link href="/dashboard" onClick={closeAllModals}>
                  <HomeIcon size={24} />
                </Link>
              </Button>
              <p className={cn(pathname === "/dashboard" ? activeLabelClasses : inactiveLabelClasses, "-mt-1")}>
                Dashboard
              </p>
            </div>
          )}

          {/* Navigation Items */}
          {visibleNavItems.map(item => (
            <div key={item.href} className="flex flex-col items-center gap-1">
              <Button
                variant="ghost-clean"
                size="icon"
                asChild
                className={cn(pathname === item.href ? activeItemClasses : inactiveItemClasses, "p-0 h-6")}
              >
                <Link href={item.href} onClick={closeAllModals}>
                  <item.icon size={24} />
                </Link>
              </Button>
              <p className={cn(pathname === item.href ? activeLabelClasses : inactiveLabelClasses, "-mt-1")}>
                {item.label}
              </p>
            </div>
          ))}

          {/* Orders - Only show if authenticated */}
          {activeAccount && ordersItem && (
            <div className="flex flex-col items-center gap-1">
              <Button
                variant="ghost-clean"
                size="icon"
                asChild
                className={cn(
                  "flex items-center p-0 h-6",
                  pathname === "/p2p-orders" ? activeItemClasses : inactiveItemClasses,
                )}
              >
                <Link href="/p2p-orders" onClick={closeAllModals}>
                  <ordersItem.icon size={24} />
                </Link>
              </Button>
              <p className={cn(pathname === "/p2p-orders" ? activeLabelClasses : inactiveLabelClasses, "-mt-1")}>
                Orders
              </p>
            </div>
          )}

          {/* More Menu - Only show if authenticated */}
          {activeAccount && (
            <div className="flex flex-col items-center gap-1">
              <Drawer
                open={isDrawerOpen}
                onOpenChange={setIsDrawerOpen}
                shouldScaleBackground={false}
                modal={true}
                dismissible={false}
              >
                <DrawerTrigger asChild>
                  <Button variant="ghost-clean" size="icon" className="p-0 h-6" onClick={closeAllModals}>
                    <BsThreeDots size={24} />
                  </Button>
                </DrawerTrigger>
                <MoreDrawer navigationItems={drawerNavigation} onOpenChange={setIsDrawerOpen} />
              </Drawer>
              <p className={cn(inactiveLabelClasses, "-mt-1")}>More</p>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
